import { useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// One-time: turn OFF the browser's built-in scroll restoration for every
// history navigation. iOS Safari otherwise remembers the y-offset of any
// URL it's ever seen and re-applies it AFTER our reset fires — which is
// why tapping a product card in the middle of a category grid used to
// open the PDP scrolled halfway down the page.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/**
 * Detect a "back / forward" style navigation from ANY of the sources the
 * browser exposes:
 *
 *   • React Router POP — SPA back-swipe on the same tab
 *   • Navigation Timing L2 `back_forward` — bfcache-restore OR the full
 *     reload Android Chrome / iOS-PWA does when the shopper hits back on
 *     a page that isn't bfcache-eligible
 *   • Legacy `performance.navigation.type === 2` — very old Safari
 *
 * When any of these are true we MUST NOT force-reset scroll — the
 * destination page (CategoryPage / SearchPage) will restore its own
 * saved offset. Failing to detect the reload-with-back case is what
 * caused the sticky "scrolls to top on mobile" bug: navType was `PUSH`
 * so ScrollToTop's setTimeout(reset, 60) fired and beat CategoryPage's
 * restore, yanking the shopper back to y=0.
 */
function isBackForwardNav(navType) {
  if (navType === "POP") return true;
  if (typeof performance === "undefined") return false;
  try {
    const nav = performance.getEntriesByType?.("navigation") || [];
    if (nav[0]?.type === "back_forward") return true;
  } catch { /* ignore */ }
  try {
    if (performance.navigation?.type === 2) return true;
  } catch { /* ignore */ }
  return false;
}

/**
 * Resets the window (and any inner scrollers) to the top on every
 * FORWARD route change (PUSH / REPLACE).  On back/forward navigations
 * — including bfcache-restore and reload-triggered-by-back — the reset
 * is skipped so pages that opted into their own scroll-restore logic
 * (e.g. CategoryPage's infinite-scroll memory) can put the shopper
 * back exactly where they were.
 *
 * Runs the reset in a *layout* effect so it fires BEFORE paint — the
 * user never sees the previous page's scroll position flash by. A
 * secondary `useEffect` re-fires the reset after paint as a safety net
 * for iOS/Chrome-on-iOS, whose WebKit occasionally reinstates the old
 * scrollY after the initial navigation commit.
 */
const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  const navType = useNavigationType();
  const skip = isBackForwardNav(navType);

  const reset = () => {
    // Multiple targets because different iOS versions honour different ones.
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  };

  useLayoutEffect(() => {
    if (skip) return; // back/forward — let the destination restore scroll
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  useEffect(() => {
    if (skip) return;
    // Fire again on the next microtask + after a short delay to catch
    // late scroll-restoration events on iOS Safari and iOS Chrome.
    const t1 = requestAnimationFrame(reset);
    const t2 = setTimeout(reset, 60);
    return () => {
      cancelAnimationFrame(t1);
      clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  return null;
};

export default ScrollToTop;
